import React from 'react';
import PropTypes from 'prop-types';
import { Building2, Mail, Phone, Globe, MapPin, CreditCard, Star } from 'lucide-react';
import { Badge } from '../../components/common';
import './VendorDetail.css';

export default function VendorDetail({ vendor }) {
    if (!vendor) return null;

    const getStatusVariant = (status) => {
        const variants = {
            'pending_registration': 'warning',
            'pending_verification': 'warning',
            'verified': 'info',
            'active': 'success',
            'suspended': 'danger',
            'blacklisted': 'danger'
        };
        return variants[status] || 'default';
    };

    const getStatusLabel = (status) => {
        const labels = {
            'pending_registration': 'Pending Registration',
            'pending_verification': 'Pending Verification',
            'verified': 'Verified',
            'active': 'Active',
            'suspended': 'Suspended',
            'blacklisted': 'Blacklisted'
        };
        return labels[status] || status;
    };

    return (
        <div className="vendor-detail">
            <div className="vendor-header">
                <div className="vendor-avatar">
                    <Building2 size={32} />
                </div>
                <div className="vendor-title">
                    <h2>{vendor.companyName || vendor.name}</h2>
                    <span className="vendor-code">{vendor.vendorCode}</span>
                </div>
                <Badge variant={getStatusVariant(vendor.status)} size="lg">
                    {getStatusLabel(vendor.status)}
                </Badge>
            </div>

            <div className="detail-grid">
                <div className="detail-section">
                    <h3>Basic Information</h3>
                    <dl className="detail-list">
                        <div className="detail-item">
                            <dt>Vendor Type</dt>
                            <dd>{vendor.vendorType}</dd>
                        </div>
                        <div className="detail-item">
                            <dt>Tax ID (NPWP)</dt>
                            <dd>{vendor.taxId || '-'}</dd>
                        </div>
                        <div className="detail-item">
                            <dt>Business License</dt>
                            <dd>{vendor.businessLicense || '-'}</dd>
                        </div>
                        <div className="detail-item">
                            <dt>Rating</dt>
                            <dd className="rating">
                                <Star size={16} className="star-icon" />
                                {vendor.averageRating?.toFixed(1) || '-'} ({vendor.totalRatings || 0} reviews)
                            </dd>
                        </div>
                    </dl>
                </div>

                <div className="detail-section">
                    <h3>Contact Information</h3>
                    <dl className="detail-list">
                        <div className="detail-item">
                            <dt><Mail size={14} /> Email</dt>
                            <dd>{vendor.email}</dd>
                        </div>
                        <div className="detail-item">
                            <dt><Phone size={14} /> Phone</dt>
                            <dd>{vendor.phone}</dd>
                        </div>
                        <div className="detail-item">
                            <dt>Contact Person</dt>
                            <dd>{vendor.contactPerson}</dd>
                        </div>
                        {vendor.website && (
                            <div className="detail-item">
                                <dt><Globe size={14} /> Website</dt>
                                <dd>
                                    <a href={vendor.website} target="_blank" rel="noopener noreferrer">
                                        {vendor.website}
                                    </a>
                                </dd>
                            </div>
                        )}
                    </dl>
                </div>

                <div className="detail-section">
                    <h3><MapPin size={16} /> Address</h3>
                    <p className="address-text">
                        {vendor.address}
                        <br />
                        {[vendor.city, vendor.province, vendor.postalCode].filter(Boolean).join(', ')}
                        <br />
                        {vendor.country}
                    </p>
                </div>

                {vendor.bankAccounts?.length > 0 && (
                    <div className="detail-section">
                        <h3><CreditCard size={16} /> Bank Account</h3>
                        {vendor.bankAccounts.map((bank, idx) => (
                            <dl key={idx} className="detail-list">
                                <div className="detail-item">
                                    <dt>Bank Name</dt>
                                    <dd>{bank.bankName}</dd>
                                </div>
                                <div className="detail-item">
                                    <dt>Account Number</dt>
                                    <dd>{bank.accountNumber}</dd>
                                </div>
                                <div className="detail-item">
                                    <dt>Account Name</dt>
                                    <dd>{bank.accountName}</dd>
                                </div>
                            </dl>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

VendorDetail.propTypes = {
    vendor: PropTypes.object
};
